import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

const UnallocatedEthManager = ({ contract, setError, setSuccess, setLoading, loading, isOwner = false }) => {
  const [unallocatedEthBalance, setUnallocatedEthBalance] = useState('0');
  const [unallocatedAmount, setUnallocatedAmount] = useState('');
  const [ethBalances, setEthBalances] = useState({
    contractBalance: '0',
    unallocated: '0',
    gasFund: '0'
  });
  
  // Token activity history
  const [tokenActivity, setTokenActivity] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Refresh counter for manual refreshes
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    if (contract) {
      loadUnallocatedEthBalance();
      loadEthBalances();
      loadTokenActivity();
    }
  }, [contract, refreshCounter]);

  const loadUnallocatedEthBalance = async () => {
    try {
      // Call the unallocatedEthBalance getter function
      const balance = await contract.unallocatedEthBalance();
      setUnallocatedEthBalance(ethers.utils.formatEther(balance));
    } catch (error) {
      console.error("Error loading unallocated ETH balance:", error);
    }
  };
  
  const loadEthBalances = async () => {
    try {
      // Get contract's total ETH balance
      const contractBalance = await contract.provider.getBalance(contract.address);
      
      // Get gas fund balance
      const gasFundBalance = await contract.getGasFundBalance();
      
      // Get unallocated ETH balance
      const unallocatedBalance = await contract.unallocatedEthBalance();
      
      setEthBalances({
        contractBalance: ethers.utils.formatEther(contractBalance),
        unallocated: ethers.utils.formatEther(unallocatedBalance),
        gasFund: ethers.utils.formatEther(gasFundBalance)
      });
      
    } catch (error) {
      console.error("Error loading ETH balances:", error);
    }
  };
  
  const loadTokenActivity = async () => {
    try {
      // Load token buy/sell activity from events
      const tokenSoldFilter = contract.filters.TokenSold();
      const tokenBoughtFilter = contract.filters.TokenBought();
      
      // Get the last 10 events
      const soldEvents = await contract.queryFilter(tokenSoldFilter, -10000, 'latest');
      const boughtEvents = await contract.queryFilter(tokenBoughtFilter, -10000, 'latest');
      
      // Process the events
      const soldActivity = await Promise.all(soldEvents.map(async (event) => {
        return {
          type: 'Sold',
          tokenAddress: event.args.tokenAddress,
          amount: event.args.amount.toString(),
          ethValue: ethers.utils.formatEther(event.args.ethReceived),
          timestamp: new Date(event.args.timestamp.toNumber() * 1000).toLocaleString(),
          txHash: event.transactionHash
        };
      }));
      
      const boughtActivity = await Promise.all(boughtEvents.map(async (event) => {
        return {
          type: 'Bought',
          tokenAddress: event.args.tokenAddress,
          amount: event.args.amount.toString(),
          ethValue: ethers.utils.formatEther(event.args.ethSpent),
          timestamp: new Date(event.args.timestamp.toNumber() * 1000).toLocaleString(),
          txHash: event.transactionHash
        };
      }));
      
      // Combine and sort by timestamp
      const allActivity = [...soldActivity, ...boughtActivity].sort((a, b) => {
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
      
      setTokenActivity(allActivity);
      
    } catch (error) {
      console.error("Error loading token activity:", error);
    }
  };
  
  const handleAddToUnallocatedEth = async () => {
    if (!unallocatedAmount || parseFloat(unallocatedAmount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const tx = await contract.addToUnallocatedEth({ 
        value: ethers.utils.parseEther(unallocatedAmount) 
      });
      await tx.wait();
      
      setSuccess("Successfully added to unallocated ETH");
      loadUnallocatedEthBalance();
      loadEthBalances();
      setUnallocatedAmount('');
    } catch (error) {
      console.error("Error adding unallocated ETH:", error);
      setError(error.message || "Failed to add unallocated ETH");
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = () => {
    setRefreshCounter(prev => prev + 1);
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Unallocated ETH Management</h2>
        <button
          className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700"
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? "Loading..." : "Refresh Data"}
        </button>
      </div>
      
      {/* Main balance display */}
      <div className="mb-6 p-4 bg-green-50 rounded-lg">
        <div className="text-lg font-medium">Unallocated ETH Balance</div>
        <div className="text-2xl font-bold text-green-600">{unallocatedEthBalance} ETH</div>
        <div className="text-sm text-gray-500 mt-1">Available for buying tokens and trading operations</div>
      </div>
      
      {/* ETH Balance Breakdown */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium mb-2">Fund ETH Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 bg-white rounded-lg border">
            <div className="text-sm text-gray-500">Contract Total</div>
            <div className="text-lg font-semibold">{ethBalances.contractBalance} ETH</div>
          </div>
          <div className="p-3 bg-white rounded-lg border">
            <div className="text-sm text-gray-500">Gas Fund</div>
            <div className="text-lg font-semibold">{ethBalances.gasFund} ETH</div>
          </div>
          <div className="p-3 bg-white rounded-lg border border-green-200">
            <div className="text-sm text-green-600">Unallocated</div>
            <div className="text-lg font-semibold text-green-600">{ethBalances.unallocated} ETH</div>
          </div>
        </div>
      </div>
      
      {/* Add ETH Controls */}
      <div className="mb-6 border rounded-lg p-4">
        <h3 className="font-medium mb-2">Add to Unallocated ETH</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount (ETH)
          </label>
          <input
            type="number"
            className="w-full p-2 border rounded-md"
            value={unallocatedAmount}
            onChange={(e) => setUnallocatedAmount(e.target.value)}
            placeholder="0.1"
            min="0"
            step="0.01"
          />
        </div>
        <div className="grid grid-cols-1 gap-4">
          <button
            className="bg-green-600 text-white py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
            onClick={handleAddToUnallocatedEth}
            disabled={loading || !unallocatedAmount}
          >
            {loading ? "Processing..." : "Add ETH"}
          </button>
        </div>
      </div>
      
      {/* Token Trading Activity */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-medium">Recent Token Trading Activity</h3>
          <button
            className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? "Hide Activity" : "Show Activity"} 
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ml-1 transition-transform ${showHistory ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        {showHistory && (
          <div className="mt-2 max-h-60 overflow-y-auto">
            {tokenActivity.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Token</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">ETH Value</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tokenActivity.map((activity, index) => (
                    <tr key={index} className={activity.type === 'Sold' ? 'bg-green-50' : 'bg-red-50'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          activity.type === 'Sold' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {activity.type}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        {activity.tokenAddress.substring(0, 6)}...{activity.tokenAddress.substring(activity.tokenAddress.length - 4)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-right font-medium">
                        {parseFloat(activity.ethValue).toFixed(6)} ETH
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-right text-gray-500">
                        {activity.timestamp}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-sm text-gray-500 text-center py-4">
                No recent token trading activity found
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Explainer section */}
      <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-400 text-green-700">
        <h4 className="font-bold">About Unallocated ETH</h4>
        <ul className="list-disc ml-5 mt-1 text-sm">
          <li className="mb-1">Unallocated ETH is used for buying tokens and trading operations</li>
          <li className="mb-1">When tokens are sold, the ETH received is automatically added to this balance</li>
          <li className="mb-1">Anyone can contribute ETH to this balance to support fund operations</li>
          <li className="mb-1">Token purchases and trades are controlled through governance</li>
          <li>Keeping a healthy unallocated ETH balance ensures trading operations can proceed without issues</li>
        </ul>
      </div>
    </div>
  );
};

export default UnallocatedEthManager;